// WordDescriptions.tsx
import React, {
  useState,
  useEffect,
  useMemo,
} from "react";
import styles from "./WordDescriptions.module.css";

// --- API Imports ---
import { fetchCourseOrderData } from "../../API/Course/CourseAPI";
import { fetchVocabularyWords } from "../../API/Vocabulary/VocabularyAPI";
import { callOpenAI } from "../../API/API";
import { buildWordDescriptionPrompt } from "../../GenAIPrompts/GenAIPrompts";
import { 
  fetchWordDescriptionExercises,
  updateWordDescriptionExercise,
  deleteWordDescriptionExercise,
  createWordDescriptionExercise,
  WordDescriptionExercise 
} from "../../API/Lesson/WordDescriptionsAPI";

// --- Language Constants ---
const AVAILABLE_LANGUAGES = [
  "English",
  "Spanish",
  "Italian",
  "French",
  "German",
  "Portuguese",
];
const LANG_CODE_MAP: { [key: string]: string } = {
  English: "EN",
  Spanish: "ES",
  Italian: "IT",
  French: "FR",
  German: "DE",
  Portuguese: "PT",
};
const PROFICIENCY_LEVELS = ["A1", "A2", "B1", "B2", "C1", "C2"];
const DIFFICULTIES = ["1", "2", "3"];
const DOMAINS = ["general", "construction", "medical", "business", "travel", "technical", "food"];

// --- Interface Definitions ---
interface CourseOrderAPIResponseItem {
  Identifier: string;
  Vocabulary_List: string;
}
interface CourseSectionProcessed {
  identifier: string;
  vocabularyListRaw: string;
  sectionNameForAPI: string;
}

interface EditModalFormState extends Partial<WordDescriptionExercise> {}

const WordDescriptions: React.FC = () => {
  // --- State ---
  const [sourceLanguage, setSourceLanguage] = useState<string>("");
  const [section, setSection] = useState<string>("");
  const [proficiencyLevel, setProficiencyLevel] = useState<string>(
    PROFICIENCY_LEVELS[2] // B1
  );
  const [difficulty, setDifficulty] = useState<string>(DIFFICULTIES[1]); // 2
  const [domain, setDomain] = useState<string>(DOMAINS[0]); // general

  const [courseSections, setCourseSections] = useState<CourseSectionProcessed[]>([]);
  const [existingExercises, setExistingExercises] = useState<WordDescriptionExercise[]>([]);
  const [filteredExercises, setFilteredExercises] = useState<WordDescriptionExercise[]>([]);
  const [baseLanguageFilter, setBaseLanguageFilter] = useState<string>("");
  const [vocabularyWords, setVocabularyWords] = useState<string[]>([]);
  const [selectedWordsBucket, setSelectedWordsBucket] = useState<Set<string>>(new Set());
  
  const [loadingExistingExercises, setLoadingExistingExercises] = useState<boolean>(false);
  const [existingExercisesError, setExistingExercisesError] = useState<string | null>(null);
  const [loadingSections, setLoadingSections] = useState<boolean>(false);
  const [loadingVocabulary, setLoadingVocabulary] = useState<boolean>(false);
  const [generatingExercises, setGeneratingExercises] = useState<boolean>(false);
  const [aiGeneratedExercises, setAiGeneratedExercises] = useState<any[] | null>(null);
  const [savingAiExercises, setSavingAiExercises] = useState<boolean>(false);
  
  const [error, setError] = useState<string | null>(null);
  const [generalStatusMessage, setGeneralStatusMessage] = useState<string | null>(null);

  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [editingExercise, setEditingExercise] = useState<WordDescriptionExercise | null>(null);
  const [editedExerciseData, setEditedExerciseData] = useState<EditModalFormState>({});

  const langCodeMap = useMemo(() => LANG_CODE_MAP, []);

  // Helper function to get language name from language code
  const getLanguageNameFromCode = (langCode: string): string => {
    const codeToNameMap: { [key: string]: string } = {
      "en": "English",
      "es": "Spanish", 
      "it": "Italian",
      "fr": "French",
      "de": "German",
      "pt": "Portuguese"
    };
    return codeToNameMap[langCode.toLowerCase()] || langCode.toUpperCase();
  };

  // --- Load course sections when sourceLanguage changes ---
  useEffect(() => {
    async function loadCourseSectionsForLang() {
      setCourseSections([]);
      setSection("");
      setExistingExercises([]);
      setExistingExercisesError(null);
      setError(null);
      setGeneralStatusMessage(null);

      if (!sourceLanguage) return;
      setLoadingSections(true);
      try {
        const langCode = langCodeMap[sourceLanguage];
        if (!langCode) throw new Error(`Invalid source language: ${sourceLanguage}`);
        const rawCourseData: CourseOrderAPIResponseItem[] =
          await fetchCourseOrderData(langCode);
        const processedSections: CourseSectionProcessed[] = rawCourseData
          .map((item) => ({
            identifier: item.Identifier,
            vocabularyListRaw: item.Vocabulary_List,
            sectionNameForAPI: item.Vocabulary_List,
          }))
          .sort(
            (a, b) => parseInt(a.identifier, 10) - parseInt(b.identifier, 10)
          );
        setCourseSections(processedSections);
      } catch (err: any) {
        setError(
          `Failed to load course sections: ${err.message || "Unknown error"}`
        );
      } finally {
        setLoadingSections(false);
      }
    }
    loadCourseSectionsForLang();
  }, [sourceLanguage, langCodeMap]);

  // --- Load vocabulary and existing exercises when section changes ---
  useEffect(() => {
    async function loadSectionData() {
      setExistingExercises([]);
      setExistingExercisesError(null);
      setVocabularyWords([]);
      setSelectedWordsBucket(new Set());
      
      if (!section) return;
      
      // Load existing exercises
      setLoadingExistingExercises(true);
      try {
        const data = await fetchWordDescriptionExercises(section);
        setExistingExercises(data);
      } catch (err: any) {
        setExistingExercisesError(
          `Failed to load existing exercises for "${section}": ${
            err.message || "Unknown error"
          }`
        );
      } finally {
        setLoadingExistingExercises(false);
      }

      // Load vocabulary words
      setLoadingVocabulary(true);
      try {
        const vocabData = await fetchVocabularyWords(section);
        const uniqueWords = Array.from(
          new Set(vocabData.map((w) => w.Targ_Word?.trim()).filter(Boolean))
        );
        setVocabularyWords(uniqueWords.sort());
      } catch (err: any) {
        console.error("Failed to load vocabulary:", err);
        setError(`Failed to load vocabulary: ${err.message || "Unknown error"}`);
      } finally {
        setLoadingVocabulary(false);
      }
    }
    loadSectionData();
  }, [section]);

  // --- Filter exercises by base language ---
  useEffect(() => {
    if (!baseLanguageFilter) {
      setFilteredExercises(existingExercises);
    } else {
      setFilteredExercises(
        existingExercises.filter(exercise => exercise.baseLanguage === baseLanguageFilter)
      );
    }
  }, [existingExercises, baseLanguageFilter]);

  // --- Handlers ---
  const handleSourceLanguageChange = (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => setSourceLanguage(e.target.value);

  const handleSectionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSection(e.target.value);
    setGeneralStatusMessage(null);
    setError(null);
  };

  const handleProficiencyChange = (e: React.ChangeEvent<HTMLSelectElement>) =>
    setProficiencyLevel(e.target.value);
  const handleDifficultyChange = (e: React.ChangeEvent<HTMLSelectElement>) =>
    setDifficulty(e.target.value);
  const handleDomainChange = (e: React.ChangeEvent<HTMLSelectElement>) =>
    setDomain(e.target.value);

  const handleBaseLanguageFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) =>
    setBaseLanguageFilter(e.target.value);

  // --- Handler for word selection ---
  const toggleWord = (word: string) => {
    setSelectedWordsBucket((prev) => {
      const next = new Set(prev);
      next.has(word) ? next.delete(word) : next.add(word);
      return next;
    });
  };

  // --- Handler to delete an existing exercise ---
  const handleDeleteExistingExercise = async (
    exerciseToDelete: WordDescriptionExercise
  ) => {
    if (
      !window.confirm(
        `Are you sure you want to delete this exercise for "${exerciseToDelete.correctOption}"?`
      )
    )
      return;
    setGeneralStatusMessage(
      `Deleting exercise ${exerciseToDelete.exerciseId}...`
    );
    try {
      await deleteWordDescriptionExercise(exerciseToDelete.sectionId, exerciseToDelete.exerciseId);
      setGeneralStatusMessage(
        `Exercise ${exerciseToDelete.exerciseId} deleted successfully.`
      );
      setExistingExercises((prev) =>
        prev.filter((exercise) => exercise.exerciseId !== exerciseToDelete.exerciseId)
      );
    } catch (err: any) {
      console.error("Failed to delete exercise:", err);
      setGeneralStatusMessage(
        `Error deleting exercise ${exerciseToDelete.exerciseId}: ${err.message}`
      );
    }
  };

  // --- Handler for opening the Edit Modal ---
  const handleOpenEditModal = (exercise: WordDescriptionExercise) => {
    setEditingExercise(exercise);
    setEditedExerciseData({ ...exercise });
    setIsEditModalOpen(true);
    setGeneralStatusMessage(null);
  };

  // --- Handler to close the Edit Modal ---
  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setEditingExercise(null);
    setEditedExerciseData({});
  };

  // --- Handler for any field changes in the Edit Modal ---
  const handleEditFormChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setEditedExerciseData((prev) => ({ ...prev, [name]: value }));
  };

  // --- Handler to actually send updates from the Edit Modal to the backend ---
  const handleUpdateEditedExercise = async () => {
    if (!editingExercise || Object.keys(editedExerciseData).length === 0) {
      setGeneralStatusMessage(
        "Error: No exercise data to update or no changes made."
      );
      return;
    }
    setGeneralStatusMessage(
      `Updating exercise ${editingExercise.exerciseId}...`
    );
    try {
      await updateWordDescriptionExercise(
        editingExercise.sectionId,
        editingExercise.exerciseId,
        editedExerciseData
      );
      setGeneralStatusMessage(
        `Exercise ${editingExercise.exerciseId} updated successfully.`
      );
      
      // Refetch the exercises to get the updated data
      try {
        const updatedExercises = await fetchWordDescriptionExercises(section);
        setExistingExercises(updatedExercises);
      } catch (fetchErr: any) {
        console.error("Failed to refetch exercises after update:", fetchErr);
        // Still close the modal even if refetch fails
      }
      
      handleCloseEditModal();
    } catch (err: any) {
      console.error("Failed to update exercise:", err);
      setGeneralStatusMessage(
        `Error updating exercise ${editingExercise.exerciseId}: ${err.message}`
      );
    }
  };

  // --- Handler for AI Generation ---
  const handleGenerateAIDescriptions = async () => {
    if (selectedWordsBucket.size === 0) {
      setGeneralStatusMessage("Please select at least one word for AI generation.");
      return;
    }

    if (!sourceLanguage || !section) {
      setGeneralStatusMessage("Please select a source language and section first.");
      return;
    }

    setGeneratingExercises(true);
    setAiGeneratedExercises(null);
    setGeneralStatusMessage("Generating AI word descriptions for all base languages...");
    setError(null);

    try {
      const selectedWordsArray = Array.from(selectedWordsBucket);
      const targetLangCode = langCodeMap[sourceLanguage];
      const targetLanguage = sourceLanguage; // Answer options in target language (Spanish, French, etc.)
      
      // Generate exercises for all available base languages except the target language
      const baseLanguagesToGenerate = AVAILABLE_LANGUAGES.filter(lang => lang !== targetLanguage);
      const allGeneratedExercises = [];
      
      for (const baseLanguageName of baseLanguagesToGenerate) {
        setGeneralStatusMessage(`Generating descriptions in ${baseLanguageName}...`);
        
        try {
          // Build the AI prompt for this base language
          const payload = buildWordDescriptionPrompt(
            selectedWordsArray,
            baseLanguageName, // Hints in this base language
            targetLanguage, // Answer options in target language
            domain,
            proficiencyLevel,
            difficulty
          );

          // Call OpenAI for this base language
          const aiResponse = await callOpenAI(payload);
          
          if (!aiResponse || !aiResponse.response) {
            console.error(`Failed to get AI response for ${baseLanguageName}`);
            continue; // Skip this language and continue with others
          }

          const responseText = aiResponse.response;

          // Parse the AI response
          let parsedDescriptions;
          try {
            parsedDescriptions = JSON.parse(responseText);
          } catch (parseErr) {
            console.error(`Failed to parse AI response for ${baseLanguageName}:`, responseText);
            continue; // Skip this language and continue with others
          }

          if (!Array.isArray(parsedDescriptions)) {
            console.error(`AI response for ${baseLanguageName} is not an array`);
            continue; // Skip this language and continue with others
          }

          // Transform AI response into WordDescriptionExercise format for this base language
          const exercisesForThisBaseLanguage = parsedDescriptions.map((item, index) => ({
            sectionId: section,
            exerciseId: `ai_generated_${Date.now()}_${baseLanguageName.toLowerCase()}_${index}`,
            wordId: item.correctOption || selectedWordsArray[index] || `word_${index}`,
            baseLanguage: langCodeMap[baseLanguageName]?.toLowerCase() || baseLanguageName.substring(0, 2).toLowerCase(),
            targetLanguage: targetLangCode?.toLowerCase() || targetLanguage.substring(0, 2).toLowerCase(),
            domain: domain,
            proficiencyLevel: proficiencyLevel,
            difficulty: difficulty,
            options: item.options || [item.correctOption || selectedWordsArray[index]],
            correctOption: item.correctOption || selectedWordsArray[index] || `word_${index}`,
            hints: item.hints || ["Description not provided"],
            createdAt: new Date().toISOString()
          }));

          allGeneratedExercises.push(...exercisesForThisBaseLanguage);

        } catch (err: any) {
          console.error(`Failed to generate descriptions for ${baseLanguageName}:`, err);
          // Continue with other languages even if one fails
        }
      }

      if (allGeneratedExercises.length === 0) {
        throw new Error("Failed to generate exercises for any base language");
      }

      setAiGeneratedExercises(allGeneratedExercises);
      setGeneralStatusMessage(
        `Successfully generated ${allGeneratedExercises.length} word description exercises across ${baseLanguagesToGenerate.length} base languages (excluding ${targetLanguage}). Review and approve them below.`
      );

    } catch (err: any) {
      console.error("Failed to generate AI descriptions:", err);
      setError(`Failed to generate AI descriptions: ${err.message || "Unknown error"}`);
      setGeneralStatusMessage(null);
    } finally {
      setGeneratingExercises(false);
    }
  };

  // --- Handler to save AI generated exercises ---
  const handleSaveAIExercises = async () => {
    if (!aiGeneratedExercises || aiGeneratedExercises.length === 0) {
      setGeneralStatusMessage("No AI generated exercises to save.");
      return;
    }

    setSavingAiExercises(true);
    setGeneralStatusMessage("Saving AI generated exercises...");

    try {
      let successCount = 0;
      let errorCount = 0;

      for (const exercise of aiGeneratedExercises) {
        try {
          // Generate a proper exerciseId for the backend
          const timestamp = Date.now();
          const randomSuffix = Math.random().toString(36).substring(2, 8);
          const exerciseId = `ide-${exercise.correctOption}-${timestamp}-${randomSuffix}`;
          
          // Prepare the exercise data with proper exerciseId
          const exerciseData = {
            ...exercise,
            exerciseId: exerciseId
          };
          
          // Remove createdAt as it may be generated by the backend
          const { createdAt, ...finalExerciseData } = exerciseData;
          
          await createWordDescriptionExercise(finalExerciseData);
          successCount++;
        } catch (err: any) {
          console.error(`Failed to save exercise for word ${exercise.correctOption}:`, err);
          errorCount++;
        }
      }

      if (successCount > 0) {
        setGeneralStatusMessage(
          `Successfully saved ${successCount} exercise${successCount !== 1 ? 's' : ''}${errorCount > 0 ? `, ${errorCount} failed` : ''}.`
        );
        
        // Refresh the existing exercises list
        try {
          const updatedExercises = await fetchWordDescriptionExercises(section);
          setExistingExercises(updatedExercises);
        } catch (fetchErr: any) {
          console.error("Failed to refetch exercises after saving:", fetchErr);
        }
        
        // Clear AI generated exercises and selected words
        setAiGeneratedExercises(null);
        setSelectedWordsBucket(new Set());
      } else {
        setGeneralStatusMessage("Failed to save any exercises. Please try again.");
      }

    } catch (err: any) {
      console.error("Error in saving AI exercises:", err);
      setGeneralStatusMessage(`Error saving exercises: ${err.message || "Unknown error"}`);
    } finally {
      setSavingAiExercises(false);
    }
  };

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>Word Descriptions Manager</h1>
      {error && (
        <div className={styles.error}>{error}</div>
      )}
      {generalStatusMessage && (
        <div className={styles.infoMessage}>
          {generalStatusMessage}
        </div>
      )}
      
      {/* --- Shared Controls: Language / Section / Proficiency / Difficulty --- */}
      <div className={styles.controlsGrid}>
        <div className={styles.formControl}>
          <label htmlFor="sourceLanguage" className={styles.label}>
            Source Language:
          </label>
          <select
            id="sourceLanguage"
            value={sourceLanguage}
            onChange={handleSourceLanguageChange}
            className={styles.input}
            disabled={loadingSections}
          >
            <option value="">Select Language</option>
            {AVAILABLE_LANGUAGES.map((lang) => (
              <option key={lang} value={lang}>
                {lang}
              </option>
            ))}
          </select>
        </div>
        <div className={styles.formControl}>
          <label htmlFor="section" className={styles.label}>
            Section:
          </label>
          <select
            id="section"
            value={section}
            onChange={handleSectionChange}
            className={styles.input}
            disabled={
              !sourceLanguage ||
              loadingSections
            }
          >
            <option value="">
              {loadingSections
                ? "Loading Sections..."
                : sourceLanguage
                ? "Select Section"
                : "Choose Language First"}
            </option>
            {!loadingSections &&
              courseSections.map((cs) => (
                <option key={cs.identifier} value={cs.sectionNameForAPI}>
                  {cs.sectionNameForAPI} (ID: {cs.identifier})
                </option>
              ))}
          </select>
        </div>
        <div className={styles.formControl}>
          <label htmlFor="proficiencyLevel" className={styles.label}>
            Proficiency Level:
          </label>
          <select
            id="proficiencyLevel"
            value={proficiencyLevel}
            onChange={handleProficiencyChange}
            className={styles.input}
          >
            {PROFICIENCY_LEVELS.map((level) => (
              <option key={level} value={level}>
                {level}
              </option>
            ))}
          </select>
        </div>
        <div className={styles.formControl}>
          <label htmlFor="difficulty" className={styles.label}>
            Difficulty (1-3):
          </label>
          <select
            id="difficulty"
            value={difficulty}
            onChange={handleDifficultyChange}
            className={styles.input}
          >
            {DIFFICULTIES.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
        </div>
        <div className={styles.formControl}>
          <label htmlFor="domain" className={styles.label}>
            Domain:
          </label>
          <select
            id="domain"
            value={domain}
            onChange={handleDomainChange}
            className={styles.input}
          >
            {DOMAINS.map((d) => (
              <option key={d} value={d}>
                {d.charAt(0).toUpperCase() + d.slice(1)}
              </option>
            ))}
          </select>
        </div>
      </div>
      
      {!loadingSections &&
        sourceLanguage &&
        courseSections.length === 0 &&
        !error && (
          <p className={styles.infoMessage}>
            No course sections found for {sourceLanguage}.
          </p>
        )}

      {/* --- Vocabulary Selection --- */}
      {section && vocabularyWords.length > 0 && (
        <div className={styles.sectionContainer}>
          <h2 className={styles.subHeading}>Select Vocabulary Words for AI Generation</h2>
          {loadingVocabulary ? (
            <p className={styles.loadingMessage}>Loading vocabulary...</p>
          ) : (
            <>
              <div className={styles.tableContainer}>
                <table className={styles.vocabTable}>
                  <thead>
                    <tr>
                      <th style={{width: '60px'}}>Select</th>
                      <th>Vocabulary Word</th>
                    </tr>
                  </thead>
                  <tbody>
                    {vocabularyWords.map((word, index) => (
                      <tr key={index}>
                        <td style={{textAlign: 'center'}}>
                          <input
                            type="checkbox"
                            checked={selectedWordsBucket.has(word)}
                            onChange={() => toggleWord(word)}
                            disabled={generatingExercises}
                          />
                        </td>
                        <td>{word}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {selectedWordsBucket.size > 0 && (
                <div className={styles.buttonContainer}>
                  <button
                    onClick={handleGenerateAIDescriptions}
                    className={`${styles.button} ${styles.actionButton}`}
                    disabled={generatingExercises || !selectedWordsBucket.size}
                  >
                    {generatingExercises
                      ? "Generating AI Descriptions..."
                      : `Generate AI Descriptions (${selectedWordsBucket.size} words)`}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {section && vocabularyWords.length === 0 && !loadingVocabulary && (
        <div className={styles.sectionContainer}>
          <p className={styles.infoMessage}>
            No vocabulary words found for this section.
          </p>
        </div>
      )}

      {/* --- AI Generated Exercises Review --- */}
      {aiGeneratedExercises && aiGeneratedExercises.length > 0 && (
        <div className={styles.generationArea}>
          <h2 className={styles.subHeading}>
            AI Generated Word Description Exercises - Review & Approve
          </h2>
          <p>
            Review the AI-generated word description exercises below. You can approve all of them at once or go back and generate new ones.
          </p>
          {aiGeneratedExercises.map((exercise, index) => (
            <div key={index} className={styles.exerciseItem}>
              <p>
                <strong>Correct Answer:</strong> {exercise.correctOption}
              </p>
              <p>
                <strong>Multiple Choice Options:</strong> {exercise.options.join(", ")}
              </p>
              <div>
                <strong>Hints:</strong>
                {exercise.hints.map((hint: { baseText: string; targetText: string } | string, hintIndex: number) => (
                  <div key={hintIndex} style={{ marginLeft: "20px", marginTop: "10px", padding: "8px", border: "1px solid #e0e0e0", borderRadius: "4px", backgroundColor: "#f9f9f9" }}>
                    <div style={{ fontWeight: "bold", color: "#1b4d8a", marginBottom: "5px" }}>
                      Hint #{hintIndex + 1}
                    </div>
                    {typeof hint === 'string' ? (
                      <p style={{ margin: "2px 0" }}><strong>{getLanguageNameFromCode(exercise.baseLanguage)}:</strong> {hint}</p>
                    ) : (
                      <>
                        <p style={{ margin: "2px 0" }}><strong>{getLanguageNameFromCode(exercise.baseLanguage)}:</strong> {hint.baseText}</p>
                        <p style={{ margin: "2px 0" }}><strong>{getLanguageNameFromCode(exercise.targetLanguage)}:</strong> {hint.targetText}</p>
                      </>
                    )}
                  </div>
                ))}
              </div>
              <p>
                <strong>Base Language:</strong> {exercise.baseLanguage} | 
                <strong> Target Language:</strong> {exercise.targetLanguage} | 
                <strong> Domain:</strong> {exercise.domain} | 
                <strong> Proficiency:</strong> {exercise.proficiencyLevel} | 
                <strong> Difficulty:</strong> {exercise.difficulty}
              </p>
            </div>
          ))}
          <div className={styles.buttonContainer}>
            <button
              onClick={handleSaveAIExercises}
              className={`${styles.button} ${styles.actionButton}`}
              disabled={savingAiExercises}
            >
              {savingAiExercises
                ? "Saving Exercises..."
                : `Approve & Save All ${aiGeneratedExercises.length} Exercises`}
            </button>
            <button
              onClick={() => setAiGeneratedExercises(null)}
              className={styles.button}
              disabled={savingAiExercises}
            >
              Cancel & Generate New
            </button>
          </div>
        </div>
      )}

      {/* --- Existing Word Description Exercises --- */}
      {section && (
        <div className={styles.sectionContainer}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
            <h2 className={styles.subHeading} style={{ marginBottom: 0 }}>
              Existing Word Description Exercises for "{section}"
            </h2>
            <button
              onClick={() => {/* TODO: Add create modal handler */}}
              className={`${styles.button} ${styles.actionButton}`}
              disabled={!section || !sourceLanguage}
            >
              Add New Exercise
            </button>
          </div>

          {/* Base Language Filter */}
          {existingExercises.length > 0 && (
            <div className={styles.filterContainer} style={{ marginBottom: '20px' }}>
              <div className={styles.formControl}>
                <label htmlFor="baseLanguageFilter" className={styles.label}>
                  Filter by Base Language:
                </label>
                <select
                  id="baseLanguageFilter"
                  value={baseLanguageFilter}
                  onChange={handleBaseLanguageFilterChange}
                  className={styles.input}
                  style={{ maxWidth: '300px' }}
                >
                  <option value="">All Base Languages</option>
                  {Array.from(new Set(existingExercises.map(exercise => exercise.baseLanguage)))
                    .sort()
                    .map(baseLang => (
                      <option key={baseLang} value={baseLang}>
                        {baseLang.toUpperCase()}
                      </option>
                    ))}
                </select>
              </div>
            </div>
          )}
          {loadingExistingExercises && (
            <p className={styles.loadingMessage}>
              Loading existing exercises...
            </p>
          )}
          {existingExercisesError && (
            <div className={styles.error}>{existingExercisesError}</div>
          )}
          {!loadingExistingExercises &&
            !existingExercisesError &&
            existingExercises.length === 0 && (
              <p className={styles.infoMessage}>
                No existing word description exercises found for this section.
              </p>
            )}
          {!loadingExistingExercises &&
            !existingExercisesError &&
            filteredExercises.length === 0 &&
            existingExercises.length > 0 && (
              <p className={styles.infoMessage}>
                No exercises found for the selected base language filter.
              </p>
            )}
          {!loadingExistingExercises &&
            !existingExercisesError &&
            filteredExercises.length > 0 && (
              <div className={styles.tableContainer}>
                <table className={styles.dataTable}>
                  <thead>
                    <tr>
                      <th>Exercise ID</th>
                      <th>Correct Answer</th>
                      <th>Options</th>
                      <th>Base/Target Lang</th>
                      <th>Domain</th>
                      <th>Proficiency</th>
                      <th>Difficulty</th>
                      <th>Hints</th>
                      <th>Created At</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredExercises.map((item) => (
                      <tr key={item.exerciseId}>
                        <td>{item.exerciseId}</td>
                        <td>{item.correctOption}</td>
                        <td style={{ maxWidth: '150px', wordWrap: 'break-word' }}>
                          {item.options ? item.options.join(", ") : "N/A"}
                        </td>
                        <td>{item.baseLanguage}/{item.targetLanguage}</td>
                        <td>{item.domain}</td>
                        <td>{item.proficiencyLevel}</td>
                        <td>{item.difficulty}</td>
                        <td style={{ maxWidth: '200px', wordWrap: 'break-word' }}>
                          {item.hints ? item.hints.map((h, idx) => 
                            `${idx + 1}. ${typeof h === 'string' ? h : h.baseText}`
                          ).join(" | ") : "N/A"}
                        </td>
                        <td>{new Date(item.createdAt).toLocaleDateString()}</td>
                        <td>
                          <button
                            onClick={() => handleOpenEditModal(item)}
                            className={`${styles.button} ${styles.editButton}`}
                            style={{ marginRight: "5px" }}
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteExistingExercise(item)}
                            className={`${styles.button} ${styles.deleteButton}`}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
        </div>
      )}

      {/* --- Edit Modal --- */}
      {isEditModalOpen && editingExercise && (
        <div className={styles.modalOverlay}>
          <div
            className={styles.modalContent}
            style={{ maxHeight: "90vh", overflowY: "auto" }}
          >
            <h2>
              Edit Word Description Exercise (ID: {editingExercise.exerciseId})
            </h2>
            <div className={styles.modalForm}>
              <div className={styles.formControlModal}>
                <label htmlFor="correctOption">Correct Answer:</label>
                <input
                  type="text"
                  id="correctOption"
                  name="correctOption"
                  value={editedExerciseData.correctOption || ""}
                  onChange={handleEditFormChange}
                  className={styles.input}
                />
              </div>
              <div className={styles.formControlModal}>
                <label htmlFor="options">Multiple Choice Options (comma-separated):</label>
                <input
                  type="text"
                  id="options"
                  name="options"
                  value={editedExerciseData.options ? editedExerciseData.options.join(", ") : ""}
                  onChange={(e) => {
                    const optionsArray = e.target.value.split(",").map(opt => opt.trim()).filter(Boolean);
                    setEditedExerciseData(prev => ({ ...prev, options: optionsArray }));
                  }}
                  className={styles.input}
                  placeholder="word1, word2, word3, word4"
                />
              </div>
              <div className={styles.formControlModal}>
                <label>Hints ({getLanguageNameFromCode(editedExerciseData.baseLanguage || "en")}):</label>
                {editedExerciseData.hints?.map((hint, index) => (
                  <div key={index} style={{ marginLeft: "10px", border: "1px solid #ccc", padding: "10px", marginBottom: "10px" }}>
                    {typeof hint === 'string' ? (
                      // Old format: simple string
                      <div>
                        <label htmlFor={`hint-${index}`}>
                          Hint #{index + 1} ({getLanguageNameFromCode(editedExerciseData.baseLanguage || "en")}):
                        </label>
                        <textarea
                          id={`hint-${index}`}
                          value={hint || ""}
                          onChange={(e) => {
                            const newHints = [...(editedExerciseData.hints || [])] as string[];
                            newHints[index] = e.target.value;
                            setEditedExerciseData(prev => ({ ...prev, hints: newHints }));
                          }}
                          className={styles.input}
                          rows={2}
                        />
                      </div>
                    ) : (
                      // New format: object with baseText and targetText
                      <>
                        <div style={{ marginBottom: "10px" }}>
                          <label htmlFor={`hint-base-${index}`}>
                            {getLanguageNameFromCode(editedExerciseData.baseLanguage || "en")} Hint:
                          </label>
                          <textarea
                            id={`hint-base-${index}`}
                            value={hint.baseText || ""}
                            onChange={(e) => {
                              const newHints = [...(editedExerciseData.hints || [])] as Array<{baseText: string; targetText: string}>;
                              newHints[index] = { ...newHints[index], baseText: e.target.value };
                              setEditedExerciseData(prev => ({ ...prev, hints: newHints }));
                            }}
                            className={styles.input}
                            rows={2}
                          />
                        </div>
                        <div>
                          <label htmlFor={`hint-target-${index}`}>
                            {getLanguageNameFromCode(editedExerciseData.targetLanguage || "es")} Hint:
                          </label>
                          <textarea
                            id={`hint-target-${index}`}
                            value={hint.targetText || ""}
                            onChange={(e) => {
                              const newHints = [...(editedExerciseData.hints || [])] as Array<{baseText: string; targetText: string}>;
                              newHints[index] = { ...newHints[index], targetText: e.target.value };
                              setEditedExerciseData(prev => ({ ...prev, hints: newHints }));
                            }}
                            className={styles.input}
                            rows={2}
                          />
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
              <div className={styles.formControlModal}>
                <label htmlFor="wordId">Word ID:</label>
                <input
                  type="text"
                  id="wordId"
                  name="wordId"
                  value={editedExerciseData.wordId || ""}
                  onChange={handleEditFormChange}
                  className={styles.input}
                />
              </div>
              <div className={styles.formControlModal}>
                <label htmlFor="domain">Domain:</label>
                <select
                  id="domain"
                  name="domain"
                  value={editedExerciseData.domain || ""}
                  onChange={handleEditFormChange}
                  className={styles.input}
                >
                  <option value="">Select Domain</option>
                  {DOMAINS.map((d) => (
                    <option key={d} value={d}>
                      {d.charAt(0).toUpperCase() + d.slice(1)}
                    </option>
                  ))}
                </select>
              </div>
              <div className={styles.formControlModal}>
                <label htmlFor="proficiencyLevel">Proficiency Level:</label>
                <select
                  id="proficiencyLevel"
                  name="proficiencyLevel"
                  value={editedExerciseData.proficiencyLevel || ""}
                  onChange={handleEditFormChange}
                  className={styles.input}
                >
                  <option value="">Select Proficiency Level</option>
                  {PROFICIENCY_LEVELS.map((level) => (
                    <option key={level} value={level}>
                      {level}
                    </option>
                  ))}
                </select>
              </div>
              <div className={styles.formControlModal}>
                <label htmlFor="difficulty">Difficulty:</label>
                <select
                  id="difficulty"
                  name="difficulty"
                  value={editedExerciseData.difficulty || ""}
                  onChange={handleEditFormChange}
                  className={styles.input}
                >
                  <option value="">Select Difficulty</option>
                  {DIFFICULTIES.map((d) => (
                    <option key={d} value={d}>
                      {d}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className={styles.modalActions}>
              <button
                onClick={handleUpdateEditedExercise}
                className={`${styles.button} ${styles.saveButton}`}
              >
                Save Changes
              </button>
              <button
                onClick={handleCloseEditModal}
                className={`${styles.button} ${styles.cancelButton}`}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WordDescriptions;